import { useEffect, useRef, useState } from 'react'

export type ScrollDirection = 'up' | 'down' | 'top'

interface UseScrollDirectionOptions {
	/** Minimum scroll delta (in px) before registering a direction change. */
	threshold?: number
	/** Above this Y position we always return 'top' (show everything). */
	topOffset?: number
}

/**
 * Track the vertical scroll direction of the window.
 *
 * Returns 'top' when the user is near the top of the page (within `topOffset`),
 * 'down' when they scrolled down past the threshold, and 'up' when they
 * scrolled up past the threshold.
 *
 * Uses requestAnimationFrame throttling — the state only updates once per
 * frame, regardless of how many scroll events fire.
 */
export function useScrollDirection({
	threshold = 6,
	topOffset = 40,
}: UseScrollDirectionOptions = {}): ScrollDirection {
	const [direction, setDirection] = useState<ScrollDirection>('top')
	const lastY = useRef(0)
	const ticking = useRef(false)

	useEffect(() => {
		lastY.current = window.scrollY

		function update() {
			const y = window.scrollY
			const delta = y - lastY.current

			if (y <= topOffset) {
				setDirection('top')
			} else if (Math.abs(delta) >= threshold) {
				setDirection(delta > 0 ? 'down' : 'up')
			}

			lastY.current = y
			ticking.current = false
		}

		function onScroll() {
			if (!ticking.current) {
				requestAnimationFrame(update)
				ticking.current = true
			}
		}

		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [threshold, topOffset])

	return direction
}
